// Promote-tab video generator — uses the user's own TRAVEL.mp4 + REVIEWS.mp4
// footage (loaded from IndexedDB) instead of AI-generated imagery. ffmpeg.wasm
// detects scene boundaries, picks 3 unique travel clips + 1 review clip per
// reel, then burns CapCut-style captions onto each clip and concatenates them
// into a single 1080x1920 MP4 the user can download.
//
// This component intentionally MIRRORS the public API of BatchVideoGenerator
// (same `posts` + `apiKey` props, same library UI, same per-post `Generate`
// + `Download`) so swapping renderers in PartnershipPortal stays a one-line
// change.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Film, Wand2, Loader2, Download, Play, CheckCircle2, AlertTriangle, Music2, FileVideo,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { getFile } from "../lib/videoStorage";
import { probeVideo, pickRandomWindows, extractClip, assembleReel, getRecentFFmpegLogs } from "../lib/videoProcessor";
import { triggerDownload } from "../lib/videoRenderer";

const SCENE_SECONDS = 3.0;       // Travel scenes (1-3) — each 3s
const CTA_SCENE_SECONDS = 5.0;   // Scene 4 (CTA / REVIEWS) — extended to 5s so viewers have time to read both text overlays, see the book, and decide to comment.
const TRAVEL_CLIPS_PER_REEL = 3; // Scenes 1-3
const REVIEW_CLIPS_PER_REEL = 1; // Scene 4
const TRAVEL_GAP_SECONDS    = 7.0; // Min separation between picked travel windows (relaxed automatically if footage is short)

const slugify = (s) =>
  (s || "video").slice(0, 50).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "video";

const LibraryVideo = ({ src, ...rest }) => {
  const ref = useRef(null);
  const handleEnded = () => {
    const v = ref.current;
    if (!v) return;
    try { v.currentTime = 0; } catch (_) { /* ignore */ }
  };
  return (
    <video
      ref={ref}
      src={src}
      controls
      controlsList="nodownload"
      preload="metadata"
      playsInline
      onEnded={handleEnded}
      className="absolute inset-0 w-full h-full object-cover"
      {...rest}
    />
  );
};

const PHASE_LABEL = {
  idle:        "Idle",
  loading:     "Loading footage",
  extracting:  "Cutting clips",
  assembling:  "Assembling reel",
  done:        "Ready",
  failed:      "Failed",
};

function initialJobs(posts) {
  return posts.map((p, i) => ({
    index: i,
    title: p.anchor_trend || `Post ${i + 1}`,
    phase: "idle",
    progress: 0,
    error: "",
    video_url: "",
    video_blob: null,
  }));
}

// Pull 4 captions (HOOK / CURIOSITY / PAYOFF / CTA) from a post — fall back
// across legacy field names so old localStorage payloads still render.
function buildCaptions(post) {
  const scenes = Array.isArray(post?.scenes) ? post.scenes.slice(0, 4) : [];
  while (scenes.length < 4) scenes.push({});
  return scenes.map((s) =>
    (s.on_screen_text || s.script || s.sentence || s.text || "").trim()
  );
}

export const PromoteBatchVideoGenerator = ({ posts = [] }) => {
  const [jobs, setJobs] = useState(() => initialJobs(posts));
  const [batchRunning, setBatchRunning] = useState(false);
  const [missing, setMissing] = useState({ travel: true, reviews: true });

  // Cache per-source probes (duration) so we don't re-read metadata for every reel.
  const cacheRef = useRef({ travel: null, reviews: null });
  const batchIdRef = useRef(0);
  const abortRef = useRef({ aborted: false });

  // On mount + whenever posts change, check IndexedDB for the two source
  // files. The actual blobs are loaded lazily inside runPost to keep memory
  // pressure low.
  useEffect(() => {
    let cancel = false;
    (async () => {
      const [t, r] = await Promise.all([getFile("travel"), getFile("reviews")]);
      if (cancel) return;
      setMissing({ travel: !t, reviews: !r });
    })();
    return () => { cancel = true; };
  }, [posts]);

  // Reset jobs + invalidate the scene cache when the posts batch changes.
  useEffect(() => {
    abortRef.current.aborted = true;
    batchIdRef.current += 1;
    abortRef.current = { aborted: false };
    cacheRef.current = { travel: null, reviews: null };
    jobs.forEach((j) => { if (j.video_url) URL.revokeObjectURL(j.video_url); });
    setJobs(initialJobs(posts));
    setBatchRunning(false);
  }, [posts]);

  const completedCount = useMemo(
    () => jobs.filter((j) => j.phase === "done" && j.video_url).length,
    [jobs]
  );

  const updateJob = (idx, patch) => {
    setJobs((prev) => prev.map((j, i) => (i === idx ? { ...j, ...patch } : j)));
  };

  // Probe both source files (HTML5 metadata read — fast, doesn't need ffmpeg).
  // Cached so we only do this once per batch.
  const ensureProbed = async (idx) => {
    if (!cacheRef.current.travel) {
      updateJob(idx, { phase: "loading", progress: 0.05 });
      const travelRow = await getFile("travel");
      if (!travelRow) throw new Error("TRAVEL footage not uploaded yet");
      const { duration } = await probeVideo(travelRow.blob);
      cacheRef.current.travel = { blob: travelRow.blob, duration };
    }
    if (!cacheRef.current.reviews) {
      const reviewsRow = await getFile("reviews");
      if (!reviewsRow) throw new Error("REVIEWS footage not uploaded yet");
      const { duration } = await probeVideo(reviewsRow.blob);
      cacheRef.current.reviews = { blob: reviewsRow.blob, duration };
    }
  };

  const runPost = async (idx) => {
    const post = posts[idx];
    if (!post) return;

    const myBatchId = batchIdRef.current;
    const myAbort = abortRef.current;
    const isStale = () => myAbort.aborted || batchIdRef.current !== myBatchId;

    updateJob(idx, { phase: "loading", progress: 0, error: "", video_url: "", video_blob: null });

    try {
      await ensureProbed(idx);
      if (isStale()) return;

      const captions = buildCaptions(post);
      const travel = cacheRef.current.travel;
      const reviews = cacheRef.current.reviews;

      // Hard fail ONLY when the source file is too short to extract even a
      // single SCENE_SECONDS-long clip. Anything else is recoverable.
      if (!travel?.duration || travel.duration < SCENE_SECONDS) {
        throw new Error(`TRAVEL footage is too short (need ≥ ${SCENE_SECONDS}s of video)`);
      }
      if (!reviews?.duration || reviews.duration < SCENE_SECONDS) {
        throw new Error(`REVIEWS footage is too short (need ≥ ${SCENE_SECONDS}s of video)`);
      }

      // 1) Pick 3 random non-overlapping TRAVEL windows (3s each) +
      //    1 random REVIEWS window. The CTA scene wants a 5s window, but if
      //    the REVIEWS footage is shorter than 5s we fall back to picking the
      //    longest usable window and pad the last frame later via tpad. This
      //    guarantees Scene 4 is always exactly 5s on screen.
      const travelPicks = pickRandomWindows(travel.duration, TRAVEL_CLIPS_PER_REEL, SCENE_SECONDS, TRAVEL_GAP_SECONDS);
      const ctaSourceSecs = Math.min(CTA_SCENE_SECONDS, reviews.duration);
      const reviewPicks = pickRandomWindows(reviews.duration, REVIEW_CLIPS_PER_REEL, ctaSourceSecs, 0);
      const reviewPick  = reviewPicks[0];

      // 2) Extract each as a normalised 1080x1920 mp4 clip. Each extraction
      //    is independently retried up to 3 times with a fresh random start
      //    if ffmpeg ever returns an empty/short clip.
      updateJob(idx, { phase: "extracting", progress: 0.3 });
      const clipBlobs = [];
      const extractWithRetry = async (sourceBlob, sourceDur, initialWindow, label, opts = {}) => {
        const sceneSecs = opts.sceneSecs || SCENE_SECONDS;       // source slice length
        const holdSecs  = opts.holdSecs  || sceneSecs;           // desired on-screen duration
        let window = initialWindow;
        let lastErr = null;
        for (let tries = 0; tries < 3; tries++) {
          try {
            const duration = Math.min(sceneSecs, window.end - window.start);
            const blob = await extractClip(sourceBlob, {
              start: window.start,
              duration,
              outputName: `${label}.mp4`,
              padToDuration: holdSecs > duration ? holdSecs : 0,
            });
            if (blob && blob.size > 1024) return blob;
            lastErr = new Error(`empty clip (size=${blob?.size || 0})`);
          } catch (e) { lastErr = e; }
          // Failed — try a different random start.
          window = pickRandomWindows(sourceDur, 1, sceneSecs, 0)[0];
        }
        const detail = lastErr?.message || "(no detail)";
        const ffLogs = getRecentFFmpegLogs().slice(-4).join(" | ");
        const which = label.startsWith("travel") ? "TRAVEL" : "REVIEWS";
        const tailNote = ffLogs ? ` · ffmpeg: ${ffLogs}` : "";
        const err = new Error(`Couldn't extract a usable clip from ${which} footage: ${detail}${tailNote}`);
        err.cause = lastErr;
        throw err;
      };

      for (let i = 0; i < travelPicks.length; i++) {
        if (isStale()) return;
        const blob = await extractWithRetry(travel.blob, travel.duration, travelPicks[i], `travel_${idx}_${i}`);
        clipBlobs.push(blob);
        updateJob(idx, { phase: "extracting", progress: 0.3 + ((i + 1) / 4) * 0.4 });
      }
      if (isStale()) return;
      {
        // CTA scene — extract up to CTA_SCENE_SECONDS of source and HOLD the
        // last frame to reach a full 5s on-screen duration. tpad in extractClip
        // takes care of the padding when ctaSourceSecs < CTA_SCENE_SECONDS.
        const blob = await extractWithRetry(reviews.blob, reviews.duration, reviewPick, `review_${idx}`, {
          sceneSecs: ctaSourceSecs,
          holdSecs: CTA_SCENE_SECONDS,
        });
        clipBlobs.push(blob);
        updateJob(idx, { phase: "extracting", progress: 0.7 });
      }

      // 3) Assemble the 4-clip reel with CapCut captions.
      updateJob(idx, { phase: "assembling", progress: 0.75 });
      const reelBlob = await assembleReel({
        clipBlobs,
        captions,
        ctaTopMessage: post.cta_top_message || "",
        outputName: `reel_${idx}.mp4`,
        fps: 30,
      });

      if (isStale()) return;
      const url = URL.createObjectURL(reelBlob);
      updateJob(idx, {
        phase: "done",
        progress: 1,
        video_url: url,
        video_blob: reelBlob,
        video_mime: "video/mp4",
      });
      toast.success(`Reel ${idx + 1} ready`);
    } catch (e) {
      // FULL error context — surfaced to console AND to the UI so we never
      // ship a "Reel N failed: unknown" message again. The error from the
      // ffmpeg layer is already enriched with the last ffmpeg log lines.
      const detail = e?.message || e?.toString?.() || "(no message)";
      console.error(`[promote] Reel ${idx + 1} failed:`, e, "\n  ffmpegLog:", e?.ffmpegLog);
      if (!isStale()) {
        updateJob(idx, { phase: "failed", error: detail });
        toast.error(`Reel ${idx + 1} failed: ${detail.slice(0, 220)}`);
      }
    }
  };

  const runAll = async () => {
    if (batchRunning) return;
    if (missing.travel || missing.reviews) {
      toast.error("Upload TRAVEL + REVIEWS footage first");
      return;
    }
    setBatchRunning(true);
    try {
      const myBatchId = batchIdRef.current;
      const myAbort = abortRef.current;
      for (let i = 0; i < posts.length; i++) {
        if (myAbort.aborted || batchIdRef.current !== myBatchId) break;
        await runPost(i);
      }
    } finally {
      setBatchRunning(false);
    }
  };

  const downloadOne = (job) => {
    const filename = `buddypass-${slugify(job.title)}-${job.index + 1}.mp4`;
    try {
      if (job.video_blob) {
        const freshUrl = URL.createObjectURL(job.video_blob);
        triggerDownload(freshUrl, filename);
        setTimeout(() => URL.revokeObjectURL(freshUrl), 8000);
        return;
      }
    } catch (_) { /* fall through */ }
    if (job.video_url) {
      triggerDownload(job.video_url, filename);
    } else {
      toast.error("Reel isn't ready yet");
    }
  };

  if (!posts?.length) return null;

  const totalProgress = jobs.length > 0
    ? jobs.reduce((s, j) => s + (j.phase === "done" ? 1 : j.progress || 0), 0) / jobs.length
    : 0;

  const missingAny = missing.travel || missing.reviews;

  return (
    <section
      data-testid="promote-video-generator"
      className="mt-10 rounded-3xl border border-white/10 bg-zinc-950/60 p-6 sm:p-8"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            <FileVideo className="h-3 w-3" strokeWidth={2} /> B-roll reel generator
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-black text-white mt-2 tracking-tight">
            Composite your <span className="text-amber-400">TRAVEL + REVIEWS</span> into 4 reels
          </h3>
          <p className="text-xs text-zinc-500 mt-1.5">
            3 random travel clips + 1 review clip per reel — captions burned CapCut-style. Everything runs in your browser.
          </p>
        </div>
        <Button
          type="button"
          onClick={runAll}
          disabled={batchRunning || missingAny}
          data-testid="generate-all-promote-videos"
          className="h-11 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white font-semibold text-sm px-5 hover:brightness-110 disabled:opacity-50"
        >
          {batchRunning ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} /> Building {completedCount}/{posts.length}…</>
          ) : (
            <><Wand2 className="mr-2 h-4 w-4" strokeWidth={2} /> Generate all {posts.length} reels</>
          )}
        </Button>
      </div>

      {missingAny && (
        <div
          data-testid="promote-video-missing-footage"
          className="mb-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[12.5px] text-amber-200 leading-relaxed"
        >
          <strong className="font-semibold">Upload required:</strong>{" "}
          {missing.travel && missing.reviews
            ? "Upload your TRAVEL.mp4 and REVIEWS.mp4 above before generating reels."
            : missing.travel
              ? "Upload your TRAVEL.mp4 above before generating reels."
              : "Upload your REVIEWS.mp4 above before generating reels."}
        </div>
      )}

      {batchRunning && (
        <div className="mb-6 h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 transition-all duration-300"
            style={{ width: `${Math.min(100, totalProgress * 100).toFixed(1)}%` }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {jobs.map((job, i) => {
          const post = posts[i];
          const busy = job.phase !== "idle" && job.phase !== "done" && job.phase !== "failed";
          return (
            <div
              key={i}
              data-testid={`promote-video-job-${i}`}
              className="rounded-2xl border border-white/10 bg-zinc-900/70 p-4 space-y-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                  Reel {String(i + 1).padStart(2, "0")} · {post?.type || "—"}
                </span>
                {job.phase === "done" && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2} />
                )}
                {job.phase === "failed" && (
                  <AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={2} />
                )}
              </div>
              <div className="text-[13px] text-zinc-200 font-semibold leading-snug line-clamp-2 min-h-[2.6em]">
                {job.title}
              </div>

              {busy && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                    {PHASE_LABEL[job.phase] || job.phase}
                  </div>
                  <div className="h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 transition-all duration-200"
                      style={{ width: `${Math.min(100, (job.progress || 0) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </div>
              )}

              {job.phase === "failed" && (
                <p className="text-[11px] text-red-300/90 leading-snug">{job.error}</p>
              )}

              <Button
                type="button"
                onClick={() => runPost(i)}
                disabled={busy || missingAny}
                data-testid={`generate-promote-video-${i}`}
                className="w-full h-9 rounded-full bg-amber-500 text-[#1c1302] font-semibold text-xs hover:bg-amber-400 disabled:opacity-50"
              >
                {busy ? (
                  <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={2} /> Working…</>
                ) : job.phase === "done" ? (
                  <><Wand2 className="mr-2 h-3.5 w-3.5" strokeWidth={2} /> Re-build</>
                ) : (
                  <><Wand2 className="mr-2 h-3.5 w-3.5" strokeWidth={2} /> Build reel</>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div
        data-testid="promote-video-library"
        className="rounded-2xl border border-white/10 bg-zinc-900/40 p-5"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
            <Film className="h-3 w-3" strokeWidth={2} /> Your reel library
          </div>
          <span className="text-[11px] text-zinc-500">
            {completedCount} of {jobs.length} ready · clears when you regenerate scripts
          </span>
        </div>

        {completedCount === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/40 p-6 text-center">
            <Play className="h-6 w-6 mx-auto text-zinc-600 mb-2" strokeWidth={1.5} />
            <p className="text-xs text-zinc-500">
              Build a reel above and it will show up here for preview + download.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {jobs.map((job, i) => {
              const post = posts[i];
              // Audio recommendations: new schema uses `trending_audio_suggestions`
              // (array of 3). Fall back to legacy singular `trending_audio_suggestion`
              // for any cached posts from before the P1 upgrade.
              const audios = Array.isArray(post?.trending_audio_suggestions)
                ? post.trending_audio_suggestions
                : (post?.trending_audio_suggestion ? [post.trending_audio_suggestion] : []);
              return job.phase === "done" && job.video_url ? (
                <div
                  key={i}
                  data-testid={`promote-library-video-${i}`}
                  className="rounded-xl border border-white/10 bg-zinc-950/70 p-2.5 space-y-2"
                >
                  <div className="relative rounded-lg overflow-hidden bg-black aspect-[9/16] border border-white/5">
                    <LibraryVideo
                      src={job.video_url}
                      data-testid={`promote-library-video-player-${i}`}
                    />
                  </div>
                  <div className="text-[11px] text-zinc-300 font-medium leading-snug line-clamp-2 min-h-[2.4em] px-0.5">
                    {job.title}
                  </div>
                  {audios.length > 0 && (
                    <div
                      data-testid={`promote-library-audio-list-${i}`}
                      className="space-y-1"
                    >
                      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-pink-300/80 px-0.5">
                        <Music2 className="h-2.5 w-2.5" strokeWidth={2.4} />
                        Trending audio · {audios.length} pick{audios.length > 1 ? "s" : ""}
                      </div>
                      {audios.map((audio, ai) => (
                        audio && audio.song ? (
                          <div
                            key={ai}
                            data-testid={`promote-library-audio-${i}-${ai}`}
                            className="flex items-center gap-1.5 rounded-lg border border-pink-500/30 bg-pink-500/5 px-2 py-1.5 text-[11px] text-pink-200"
                            title={audio.why || ""}
                          >
                            <Music2 className="h-3 w-3 shrink-0" strokeWidth={2} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate">
                                <span className="font-semibold">{audio.song}</span>
                                {audio.artist && <span className="text-pink-300/70"> · {audio.artist}</span>}
                              </div>
                              {(audio.vibe || audio.bpm_range) && (
                                <div className="text-[9.5px] text-pink-300/60 truncate">
                                  {audio.vibe || ""}{audio.vibe && audio.bpm_range ? " · " : ""}{audio.bpm_range ? `${audio.bpm_range} BPM` : ""}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null
                      ))}
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={() => downloadOne(job)}
                    data-testid={`promote-download-video-${i}`}
                    className="w-full h-8 rounded-full bg-emerald-500 text-black font-semibold text-[11px] hover:bg-emerald-400"
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                    Download MP4
                  </Button>
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoteBatchVideoGenerator;
